// Asistencia: marcaciones de entrada/salida y procesamiento a novedades
// (horas extras Art. 55, recargo nocturno Art. 49, faltas Art. 54).
// Nómina → Movimientos → Asistencia.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, fecha, fieldCls, nomRpc, useDialogoEnLinea, type EmpleadoRow, type MarcacionRow, hoyISO } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const hoy = hoyISO();
const mesIni = hoy.slice(0, 8) + '01';
const hhmmDe = (ts: string | null) => (ts ? new Date(ts).toTimeString().slice(0, 5) : '—');

interface ResumenProc {
  marcaciones_procesadas: number; horas_suplementarias: number; horas_extraordinarias: number;
  horas_nocturnas: number; faltas_generadas: number; exceso_no_generado: number;
  jornadas_liquidadas_intactas: number;
}

export function Asistencia() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [desde, setDesde] = useState(mesIni);
  const [hasta, setHasta] = useState(hoy);
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<MarcacionRow[]>({
    queryKey: ['nom-marcaciones', desde, hasta],
    queryFn: () => nomRpc<MarcacionRow[]>('nom_marcaciones', { _desde: desde, _hasta: hasta, _empleado_id: null }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const t = q.trim().toLowerCase();
  const filas = t ? rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t)) : rows;

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ empleado_id: '', fecha: hoy, entrada: '08:00', salida: '17:00', observacion: '' });
  const [generarFaltas, setGenerarFaltas] = useState(true);
  const [resumen, setResumen] = useState<ResumenProc | null>(null);

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-marcaciones'] });
    void qc.invalidateQueries({ queryKey: ['nom-novedades'] });
  };
  const marcar = useMutation({
    mutationFn: () => {
      const entrada_at = `${form.fecha}T${form.entrada}:00`;
      // salida menor o igual a la entrada = jornada nocturna → día siguiente
      const fSalida = form.salida <= form.entrada
        ? new Date(new Date(form.fecha).getTime() + 86400000).toISOString().slice(0, 10)
        : form.fecha;
      const salida_at = form.salida ? `${fSalida}T${form.salida}:00` : null;
      return nomRpc('nom_marcar', { _data: { empleado_id: form.empleado_id, fecha: form.fecha, entrada_at, salida_at, observacion: form.observacion || null } });
    },
    onSuccess: () => { toast.success('Marcación registrada'); setAbierto(false); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_marcacion_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Marcación eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const procesar = useMutation({
    mutationFn: () => nomRpc<ResumenProc>('nom_asistencia_procesar', { _desde: desde, _hasta: hasta, _generar_faltas: generarFaltas }),
    onSuccess: (r) => { setResumen(r); toast.success('Asistencia procesada: novedades generadas'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalAtraso = filas.reduce((s, m) => s + Number(m.atraso_min || 0), 0);
  const totalHoras = filas.reduce((s, m) => s + (m.horas != null ? Number(m.horas) : 0), 0);
  const headersExp = ['N°', 'Fecha', 'Empleado', 'Turno', 'Entrada', 'Salida', 'Atraso', 'Horas', 'Origen', 'Procesada'];
  const filasExport: Celda[][] = [
    ...filas.map((m, i): Celda[] => [
      i + 1, `${fecha(m.fecha)}${m.laborable === false ? ' (descanso)' : ''}`, m.razon_social, m.turno ?? 'Sin turno',
      hhmmDe(m.entrada_at), m.salida_at ? hhmmDe(m.salida_at) : 'Abierta',
      m.atraso_min ? Number(m.atraso_min) : (m.procesada_at ? 0 : ''), m.horas != null ? Number(m.horas) : '',
      m.origen, m.procesada_at ? 'Sí' : 'No',
    ]),
    ['', '', '', '', '', 'TOTAL', totalAtraso, totalHoras, '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Asistencia</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`asistencia-${fechaLocal()}`} titulo="Asistencia"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 6, 7]} disabled={filas.length === 0}
          />
          <Button onClick={() => { setForm({ empleado_id: '', fecha: hoy, entrada: '08:00', salida: '17:00', observacion: '' }); setAbierto(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Registrar marcación
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Procesar compara cada marcación contra el turno del empleado y genera las novedades: suplementarias 50%
        (topes Art. 55), extraordinarias 100% en días de descanso, recargo nocturno 25% (19h00–06h00) y faltas por
        día laborable sin marcación (respeta vacaciones y permisos). Re-procesar regenera solo lo no liquidado en un rol.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Filtrar"><input className={`${fieldCls} w-64`} placeholder="Nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} /></Campo>
        <div className="pb-1"><Switch checked={generarFaltas} onChange={setGenerarFaltas} label="Generar faltas" /></div>
        <Button variant="outline" isLoading={procesar.isPending}
          onClick={async () => { if (await dlg.confirmar('¿Procesar la asistencia?', { detalle: `Se procesará del ${desde} al ${hasta} y se generarán las novedades.`, aceptar: 'Procesar' })) procesar.mutate(); }}>
          <Play className="w-4 h-4 mr-1" /> Procesar → novedades
        </Button>
      </div>

      {resumen && (
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Marcaciones: {resumen.marcaciones_procesadas}</span>
          <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Supl. 50%: {Number(resumen.horas_suplementarias)} h</span>
          <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Extra 100%: {Number(resumen.horas_extraordinarias)} h</span>
          <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Nocturnas 25%: {Number(resumen.horas_nocturnas)} h</span>
          <span className="px-2 py-1 rounded-full bg-urgente-soft text-urgente">Faltas: {resumen.faltas_generadas}</span>
          {Number(resumen.exceso_no_generado) > 0 && <span className="px-2 py-1 rounded-full bg-pendiente-soft text-pendiente">Exceso sobre topes Art. 55: {Number(resumen.exceso_no_generado)} h</span>}
          {resumen.jornadas_liquidadas_intactas > 0 && <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground">Liquidadas intactas: {resumen.jornadas_liquidadas_intactas}</span>}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Marcaciones ({filas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Turno</th>
              <th className="py-2 pr-3">Entrada</th><th className="py-2 pr-3">Salida</th>
              <th className="py-2 pr-3 text-right">Atraso</th><th className="py-2 pr-3 text-right">Horas</th>
              <th className="py-2 pr-3">Origen</th><th className="py-2 pr-3">Procesada</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && !isFetching && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin marcaciones en el rango</td></tr>}
              {filas.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{fecha(m.fecha)}{m.laborable === false && <span className="text-xs text-muted-foreground ml-1">(descanso)</span>}</td>
                  <td className="py-2 pr-3">{m.razon_social}<div className="text-xs text-muted-foreground font-mono">{m.identificacion}</div></td>
                  <td className="py-2 pr-3">{m.turno ?? <span className="text-pendiente">Sin turno</span>}</td>
                  <td className="py-2 pr-3 font-mono">{hhmmDe(m.entrada_at)}</td>
                  <td className="py-2 pr-3 font-mono">{m.salida_at ? hhmmDe(m.salida_at) : <span className="text-pendiente">Abierta</span>}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.atraso_min ? <span className="text-urgente">{m.atraso_min} min</span> : (m.procesada_at ? '0' : '—')}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">
                    {m.horas != null ? Number(m.horas) : '—'}
                    {m.extra_no_generada != null && <span className="text-xs text-pendiente ml-1" title="Exceso sobre los topes del Art. 55 (no generado)">+{Number(m.extra_no_generada)}!</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{m.origen}</td>
                  <td className="py-2 pr-3">{m.procesada_at ? <span className="px-2 py-0.5 rounded-full text-xs bg-estable-soft text-estable">Sí</span> : <span className="text-xs text-muted-foreground">No</span>}</td>
                  <td className="py-2 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar (borra sus novedades no liquidadas)"
                      onClick={async () => { if (await dlg.confirmar('¿Eliminar la marcación?', { detalle: 'Se borran sus novedades no liquidadas.', peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(m.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar marcación" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Fecha de la jornada *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
              <Campo label="Entrada *"><input type="time" className={fieldCls} value={form.entrada} onChange={(e) => setForm({ ...form, entrada: e.target.value })} /></Campo>
              <Campo label="Salida"><input type="time" className={fieldCls} value={form.salida} onChange={(e) => setForm({ ...form, salida: e.target.value })} /></Campo>
            </div>
            {form.salida && form.salida <= form.entrada && <div className="text-xs text-muted-foreground">La salida se registrará al día siguiente (jornada nocturna).</div>}
            <Campo label="Observación"><input className={fieldCls} value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={marcar.isPending} disabled={!form.empleado_id || !form.fecha || !form.entrada} onClick={() => marcar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
